import {
  applyRate,
  type BasisPoints,
  DEFAULT_ROUNDING,
  type Money,
  type RoundingMode,
  pow10,
  zero,
} from "@tovi/shared/money";
import type { OrderLine } from "./types.ts";

/**
 * Promotional campaigns (`MARKETING_PROMOTIONAL_CAMPAIGN`).
 *
 * The source ships three named offers and they are one rule with three sets of
 * numbers *(verified — "Half Price on Second Item", "Buy1Free1", "N-th Item
 * Discount")*: every Nth item across the basket comes off by a percentage.
 * Half price on the second is N=2 at 50%; buy one get one free is N=2 at 100%.
 * Encoding them as three rules would be three places for the arithmetic to
 * disagree.
 *
 * Two decisions the source states outright, and both are the whole difficulty:
 *
 *  - **The cheapest items are the ones discounted.** "When multiple items
 *    participate, lower-priced items get discounts" — its own example is a $5
 *    burger and $3 fries giving the fries 50% off. A campaign that discounted
 *    the dearest would cost a merchant more than they agreed to, on every
 *    basket, silently.
 *  - **Consecutive or not.** "For 'half price on second item', consecutive
 *    discounts mean buying 4 items gives 2 items at half price" — so
 *    consecutive repeats every N, and non-consecutive fires once.
 *
 * The unit, not the line, is what counts. Two of a dish on one line is two
 * items to a customer reading a "second item half price" sign, and a rule that
 * counted lines would make the offer depend on how the cashier keyed it.
 */

export type Campaign = {
  readonly id: string;
  readonly name: string;
  /** Every Nth item is discounted. 2 is "the second one". */
  readonly nthItem: number;
  /** How much comes off that item. 10000 is free. */
  readonly discountBp: BasisPoints;
  /** Repeat every N, or fire once. */
  readonly isConsecutive: boolean;
  /**
   * Which items take part. Empty or absent is the whole menu — which is what a
   * merchant means when they tick nothing.
   */
  readonly itemIds?: readonly string[];
};

/** What a campaign took off one line. */
export type CampaignDiscount = {
  readonly lineId: string;
  readonly campaignId: string;
  readonly amount: Money;
};

/**
 * What each campaign takes off a basket.
 *
 * Returns per line so the result can land as a discount on the row it came
 * from — a promotional discount that appeared only as an order total could not
 * be reported per item, and `09-reports.md` asks for exactly that.
 *
 * Campaigns are applied one after another over the *same* unit prices rather
 * than compounding: two offers on one basket each discount the units they
 * qualify for, and a unit already discounted by one is not discounted again.
 * Compounding would let two campaigns take more than the item is worth.
 */
export function campaignDiscounts(
  lines: readonly OrderLine[],
  campaigns: readonly Campaign[],
  currency: Money["currency"],
  mode: RoundingMode = DEFAULT_ROUNDING,
): CampaignDiscount[] {
  if (campaigns.length === 0) return [];

  // One entry per *unit*, priced at what that unit costs before any discount.
  const units: { lineId: string; itemId: string | undefined; price: Money }[] = [];
  for (const line of lines) {
    const perUnit = pow10(line.quantity.scale);
    // A weighed line is not a count of items — 1.250 kg is one thing on a
    // plate — so it takes part as a single unit at its own price.
    const whole = line.quantity.value % perUnit === 0n ? line.quantity.value / perUnit : 1n;
    if (whole <= 0n) continue;

    let unitPrice = line.unitPrice;
    for (const modifier of line.modifiers ?? []) {
      unitPrice = { amount: unitPrice.amount + modifier.amount, currency };
    }

    for (let index = 0n; index < whole && index < 1000n; index++) {
      units.push({ lineId: line.id, itemId: line.itemId, price: unitPrice });
    }
  }

  const spent = new Set<number>();
  const found: CampaignDiscount[] = [];

  for (const campaign of campaigns) {
    if (campaign.nthItem < 2) continue;

    const eligible = units
      .map((unit, index) => ({ ...unit, index }))
      .filter((unit) => !spent.has(unit.index))
      .filter(
        (unit) =>
          !campaign.itemIds ||
          campaign.itemIds.length === 0 ||
          (unit.itemId !== undefined && campaign.itemIds.includes(unit.itemId)),
      )
      // Dearest first, so that counting to N lands on the cheapest — the
      // source's own example, and the only ordering that does not overcharge
      // the merchant.
      .sort((a, b) => Number(b.price.amount - a.price.amount));

    for (
      let position = campaign.nthItem;
      position <= eligible.length;
      position += campaign.nthItem
    ) {
      const unit = eligible[position - 1];
      if (!unit) break;

      const amount = applyRate(unit.price, campaign.discountBp, mode);
      if (amount.amount > 0n) {
        found.push({ lineId: unit.lineId, campaignId: campaign.id, amount });
        spent.add(unit.index);
      }
      if (!campaign.isConsecutive) break;
    }
  }

  return found.filter((discount) => discount.amount.amount > 0n);
}

/** The campaign discounts summed per line, in the order the lines were given. */
export function campaignDiscountsByLine(
  lines: readonly OrderLine[],
  campaigns: readonly Campaign[],
  currency: Money["currency"],
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money[] {
  const found = campaignDiscounts(lines, campaigns, currency, mode);
  return lines.map((line) => {
    let total = zero(currency);
    for (const discount of found) {
      if (discount.lineId === line.id)
        total = { amount: total.amount + discount.amount.amount, currency };
    }
    return total;
  });
}
